import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApi } from '../config/api';
import SmartImage from './SmartImage';
import { formatCurrency } from '../utils/formatters';
import '../styles/navbar.css';
import './Cart.css';

interface SharedDraftData {
  quote: {
    draft_id: string;
    draft_number: string;
    status: string;
    subtotal: number;
    shipping_cost: number;
    tax_amount: number;
    total_amount: number;
    notes?: string;
    created_at: string;
    expires_at?: string;
    company_name?: string;
    shipping_address?: {
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
  };
  items: Array<{
    id: string;
    product: {
      id: number;
      sku: string;
      name: string;
      image_url?: string;
    };
    quantity: number;
    unit_price: number;
    total_price: number;
  }>;
  is_expired: boolean;
  created_by?: string;
}

const SharedDraft: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<SharedDraftData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [logoError, setLogoError] = useState(false);

  // Format draft number from Q-2025-0911-0001 to DFT-0911-0001
  const formatDraftNumber = (quoteNumber: string) => {
    if (!quoteNumber) return '';
    const parts = quoteNumber.split('-');
    if (parts.length >= 4) {
      return `DFT-${parts[2]}-${parts[3]}`;
    }
    return `DFT-${quoteNumber.slice(-8)}`;
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchSharedDraft = async () => {
      try {
        // Public endpoint - no auth required
        const response = await publicApi.get(`/drafts/shared/${token}`);

        // Sort items: group bundled items with their parent products
        const rawItems = response.data.items || [];

        // Create a map of parent product IDs to their bundled items
        const bundledItemsMap = new Map<number, any[]>();
        const parentItems: any[] = [];

        // Separate parent items from bundled items
        rawItems.forEach((item: any) => {
          if (item.is_bundled_item && item.bundled_with_product_id) {
            // This is a bundled item
            const parentId = item.bundled_with_product_id;
            if (!bundledItemsMap.has(parentId)) {
              bundledItemsMap.set(parentId, []);
            }
            bundledItemsMap.get(parentId)!.push(item);
          } else {
            // This is a parent/normal item
            parentItems.push(item);
          }
        });

        // Sort parent items by price descending
        parentItems.sort((a, b) => b.unit_price - a.unit_price);

        // Build final sorted list: parent followed by its bundled items
        const sortedItems: any[] = [];
        parentItems.forEach(parent => {
          sortedItems.push(parent);
          // Add bundled items right after their parent
          const bundledItems = bundledItemsMap.get(parent.product.id) || [];
          sortedItems.push(...bundledItems);
        });

        // Update response data with sorted items
        response.data.items = sortedItems;

        setDraft(response.data);
        setError('');
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('This draft link has expired or is invalid.');
        } else {
          setError('Failed to load draft details');
        }
        console.error('Shared draft fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedDraft();
    }
  }, [token]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleProceedToCheckout = () => {
    // Store draft data in sessionStorage for anonymous checkout
    if (draft) {
      sessionStorage.setItem('sharedDraftData', JSON.stringify({
        quote_id: draft.quote.draft_id,
        quote_number: draft.quote.draft_number,
        status: draft.quote.status,
        company_name: draft.quote.company_name || '',
        shipping_address: draft.quote.shipping_address,
        contact_name: draft.quote.contact_name || '',
        contact_email: draft.quote.contact_email || '',
        contact_phone: draft.quote.contact_phone || '',
        items: draft.items,
        subtotal: draft.quote.subtotal,
        shipping_cost: draft.quote.shipping_cost,
        tax_amount: draft.quote.tax_amount,
        total_amount: draft.quote.total_amount,
        notes: draft.quote.notes
      }));
      sessionStorage.setItem('shareToken', token || '');
      // Redirect to anonymous checkout
      navigate('/checkout/anonymous');
    }
  };

  if (loading) {
    return (
      <div className="shared-quote-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
        {/* Top Bar */}
        <div className="unified-top-bar" style={{
          background: '#4D64C1',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1040,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '0 15px'
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-center align-items-center">
              <span style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: 700,
                letterSpacing: '-1px'
              }}>PureHD</span>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3" style={{ fontSize: '18px', color: '#6c757d' }}>Loading draft details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-quote-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
        {/* Top Bar */}
        <div className="unified-top-bar" style={{
          background: '#4D64C1',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1040,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '0 15px'
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center">
              <span style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: 700,
                letterSpacing: '-1px'
              }}>PureHD</span>
              <span style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: '500'
              }}>
                <i className="fas fa-phone-alt me-2"></i>
                (888) 787-3431
              </span>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="text-center" style={{ maxWidth: '500px' }}>
            <div className="alert alert-danger" style={{ borderRadius: '10px', padding: '20px' }}>
              <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '24px' }}></i>
              <h4 className="mb-3">Unable to Load Draft</h4>
              <p>{error}</p>
            </div>
            <div className="d-flex gap-3 justify-content-center">
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/login')}>
                Go to Login
              </button>
              <button className="btn btn-outline-primary btn-lg" type="button">
                Call Support: (888) 787-3431
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!draft || !draft.quote) {
    return (
      <div className="shared-quote-wrapper" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
        {/* Top Bar */}
        <div className="unified-top-bar" style={{
          background: '#4D64C1',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1040,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '0 15px'
        }}>
          <div className="container-fluid">
            <div className="d-flex justify-content-center align-items-center">
              <span style={{
                color: 'white',
                fontSize: '28px',
                fontWeight: 700,
                letterSpacing: '-1px'
              }}>PureHD</span>
            </div>
          </div>
        </div>
        <div className="flex-grow-1 d-flex align-items-center justify-content-center">
          <div className="text-center">
            <div className="alert alert-warning" style={{ borderRadius: '10px', padding: '20px' }}>
              <i className="fas fa-exclamation-triangle me-2" style={{ fontSize: '24px' }}></i>
              <h4 className="mb-0">Draft not found</h4>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isExpired = draft.is_expired || (draft.quote && draft.quote.expires_at && new Date(draft.quote.expires_at) < new Date());
  const isDesktop = windowWidth >= 992;
  const isMobile = windowWidth < 768;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa' }}>
      {/* Top Bar */}
      <div style={{
        background: '#4D64C1',
        height: '100px',
        display: 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 1040,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <a href="https://www.purehd.com" target="_blank" rel="noopener noreferrer">
                <img
                  src="/img/PureHD_Logo.png"
                  alt="PureHD"
                  style={{
                    height: '55px',
                    width: 'auto',
                    display: 'block',
                    filter: 'brightness(0) invert(1)',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </a>
            </div>
            <div className="text-center" style={{
              color: 'white',
              lineHeight: '1.4'
            }}>
              <div style={{ fontSize: '15px', fontWeight: '500', marginBottom: '2px' }}>
                Order PureHD hotel room equipment here…
              </div>
              <div style={{ fontSize: '13px', opacity: 0.95, marginBottom: '2px' }}>
                Set-Top Boxes • Remotes • Cables • Power Supplies
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>
                Support: <span style={{ color: 'white' }}>(888) 787-3431</span>
              </div>
            </div>
            <div>
              <button
                className="btn btn-outline-light"
                onClick={() => navigate('/login')}
                style={{ fontSize: '14px' }}
              >
                <i className="fas fa-sign-in-alt me-2"></i>
                Login
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1" style={{ padding: '30px 0' }}>
        <div className="container-fluid" style={{ maxWidth: '1400px', padding: '0 20px' }}>
          {/* Page Header */}
          <div className="mb-4">
            <h1 style={{ fontSize: '28px', fontWeight: '600', color: '#2c3e50', marginBottom: '10px' }}>
              Draft Order #{formatDraftNumber(draft.quote.draft_number)}
            </h1>
            <p className="text-muted" style={{ fontSize: '16px', marginBottom: 0 }}>
              {draft.quote.company_name || 'Guest Order'}
            </p>
          </div>

          {/* Expiration / Converted Warning */}
          {(isExpired || draft.quote.status === 'CONVERTED') && (
            <div className="alert alert-warning mb-4" style={{ borderLeft: '4px solid #ef9221', display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
              {isExpired ? (
                <>
                  <span><i className="fas fa-exclamation-triangle me-2" style={{ color: '#ff9800' }}></i><strong>This draft has expired.</strong></span>
                  <span>Prices shown may no longer be valid.</span>
                  <span>Please contact support for current pricing at (888) 787-3431</span>
                </>
              ) : (
                <>
                  <span><i className="fas fa-exclamation-triangle me-2" style={{ color: '#ff9800' }}></i><strong>This draft has already been checked out.</strong></span>
                  <span>The shared link is informational only.</span>
                  <span>If you need to purchase again, please create a new draft or order.</span>
                </>
              )}
            </div>
          )}

          {/* Main Layout */}
          <div style={{
            display: 'flex',
            flexWrap: isDesktop ? 'nowrap' : 'wrap',
            gap: '30px'
          }}>
            {/* Left Section - Items */}
            <div style={{
              flex: isDesktop ? '0 0 66.66667%' : '0 0 100%',
              maxWidth: isDesktop ? 'calc(66.66667% - 15px)' : '100%'
            }}>
              <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '20px' }}>
                <div className="card-header" style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '15px 20px'
                }}>
                  <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                    Items ({draft.items.length})
                  </h5>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th style={{ padding: '12px 20px', fontWeight: '600', color: '#495057' }}>Product</th>
                          <th className="text-center" style={{ padding: '12px', fontWeight: '600', color: '#495057' }}>Quantity</th>
                          <th className="text-end" style={{ padding: '12px', fontWeight: '600', color: '#495057' }}>Unit Price</th>
                          <th className="text-end" style={{ padding: '12px 20px', fontWeight: '600', color: '#495057' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {draft.items.map(item => (
                          <tr key={item.id}>
                            <td style={{ padding: '15px 20px' }}>
                              <div className="d-flex align-items-center">
                                <div className="me-3" style={{ width: '60px', height: '60px', minWidth: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                                  <SmartImage
                                    src={item.product.image_url}
                                    alt={item.product.name}
                                    className="cart-item-image"
                                    productName={item.product.name}
                                    isPublic={true}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '4px' }}>
                                    {item.product.name}
                                  </div>
                                  <small className="text-muted">{item.product.sku}</small>
                                </div>
                              </div>
                            </td>
                            <td className="text-center align-middle" style={{ padding: '15px 12px' }}>
                              <span style={{ fontWeight: '500' }}>{item.quantity}</span>
                            </td>
                            <td className="text-end align-middle" style={{ padding: '15px 12px' }}>
                              {formatCurrency(item.unit_price)}
                            </td>
                            <td className="text-end align-middle" style={{ padding: '15px 20px' }}>
                              <span style={{ fontWeight: '600', color: '#2c3e50' }}>
                                {formatCurrency(item.total_price)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {draft.quote.notes && (
                <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
                  <div className="card-header" style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    padding: '15px 20px'
                  }}>
                    <h6 className="mb-0" style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
                      <i className="fas fa-sticky-note me-2"></i>Notes
                    </h6>
                  </div>
                  <div className="card-body" style={{ padding: '20px' }}>
                    <p className="mb-0" style={{ color: '#495057', lineHeight: '1.6' }}>
                      {draft.quote.notes}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Section - Summary */}
            <div style={{
              flex: isDesktop ? '0 0 33.33333%' : '0 0 100%',
              maxWidth: isDesktop ? 'calc(33.33333% - 15px)' : '100%'
            }}>
              {/* Summary Card */}
              <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px', marginBottom: '20px' }}>
                <div className="card-header" style={{
                  backgroundColor: '#4D64C1',
                  color: 'white',
                  padding: '15px 20px',
                  borderRadius: '7px 7px 0 0'
                }}>
                  <h6 className="mb-0" style={{ fontSize: '16px', fontWeight: '600' }}>Order Summary</h6>
                </div>
                <div className="card-body" style={{ padding: '20px' }}>
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Subtotal:</span>
                    <span style={{ fontSize: '15px', fontWeight: '500' }}>{formatCurrency(draft.quote.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Shipping:</span>
                    <span style={{ fontSize: '15px', fontWeight: '500' }}>{formatCurrency(draft.quote.shipping_cost)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Tax:</span>
                    <span style={{ fontSize: '15px', fontWeight: '500' }}>
                      {draft.quote.tax_amount && draft.quote.tax_amount > 0 ? (
                        formatCurrency(draft.quote.tax_amount)
                      ) : (
                        <span style={{ color: '#6c757d', fontStyle: 'italic' }}>To be calculated</span>
                      )}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center pt-2">
                    <span className="h5 mb-0" style={{ fontWeight: '600', color: '#2c3e50' }}>Total:</span>
                    <span className="h4 mb-0" style={{ fontWeight: '700', color: '#4D64C1' }}>
                      {draft.quote.tax_amount && draft.quote.tax_amount > 0 ? (
                        formatCurrency(draft.quote.total_amount)
                      ) : (
                        <span>
                          {formatCurrency(draft.quote.subtotal + draft.quote.shipping_cost)}
                          <small style={{ fontSize: '14px', color: '#6c757d', fontWeight: '400' }}> + tax</small>
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <div className="card-body" style={{ padding: '20px' }}>
                  <div className="mb-3" style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                    <div style={{ fontSize: '13px', color: '#6c757d', lineHeight: '1.8' }}>
                      <div className="d-flex justify-content-between mb-1">
                        <span>Created:</span>
                        <span style={{ fontWeight: '500', color: '#495057' }}>{formatDate(draft.quote.created_at)}</span>
                      </div>
                      {draft.quote.expires_at && (
                        <div className="d-flex justify-content-between mb-1">
                          <span>Expires:</span>
                          <span style={{ fontWeight: '500', color: '#495057' }}>{formatDate(draft.quote.expires_at)}</span>
                        </div>
                      )}
                      {draft.created_by && (
                        <div className="d-flex justify-content-between">
                          <span>Shared by:</span>
                          <span style={{ fontWeight: '500', color: '#495057' }}>{draft.created_by}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {!isExpired ? (
                    <>
                      <button
                        className="btn w-100 mb-2"
                        onClick={handleProceedToCheckout}
                        style={{
                          backgroundColor: 'var(--color-success, #2E7D32)',
                          color: 'white',
                          border: 'none',
                          padding: '12px',
                          fontSize: '16px',
                          fontWeight: '500',
                          borderRadius: '6px'
                        }}
                      >
                        Proceed to Checkout
                      </button>
                      <p className="text-center" style={{ fontSize: '13px', color: '#6c757d', marginBottom: 0 }}>
                        Original prices will be honored at checkout
                      </p>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-warning w-100 mb-2"
                        onClick={() => navigate('/login')}
                        style={{
                          padding: '12px',
                          fontSize: '16px',
                          fontWeight: '500',
                          borderRadius: '6px'
                        }}
                      >
                        Login to Order with Current Prices
                      </button>
                      <p className="text-center" style={{ fontSize: '13px', color: '#6c757d', marginBottom: 0 }}>
                        This draft has expired. Current prices will apply.
                      </p>
                    </>
                  )}
                </div>
              </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#2c3e50',
        borderTop: '1px solid #34495e',
        padding: '30px 0 20px',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: isDesktop ? 'nowrap' : 'wrap',
            gap: isDesktop ? '40px' : '20px',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}>
            <div style={{
              flex: isDesktop ? '0 0 auto' : '0 0 100%',
              maxWidth: isDesktop ? '25%' : '100%',
              textAlign: isDesktop ? 'left' : 'center'
            }}>
              <h6 style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px', color: '#ecf0f1' }}>PureHD</h6>
              <p style={{ fontSize: '12px', color: '#95a5a6', marginBottom: 0, lineHeight: '1.5' }}>
                Experience the future of television with PureHD's cutting-edge solutions.
              </p>
            </div>

            <div style={{
              flex: isDesktop ? '0 0 auto' : '0 0 100%',
              maxWidth: isDesktop ? '20%' : '100%',
              textAlign: isDesktop ? 'left' : 'center'
            }}>
              <h6 style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px', color: '#ecf0f1' }}>Support</h6>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '5px' }}>
                  <span style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>
                    <i className="fas fa-phone-alt me-2"></i>(888) 787-3431
                  </span>
                </li>
                <li style={{ marginBottom: '5px' }}>
                  <a href="mailto:support@purehd.com" style={{ color: '#bdc3c7', textDecoration: 'none', fontSize: '12px' }}>
                    <i className="fas fa-envelope me-2"></i>support@purehd.com
                  </a>
                </li>
                {/* Help Center link - hidden until help center is updated
                <li>
                  <a href="https://www.purehd.com/support/" target="_blank" rel="noopener noreferrer"
                     style={{ color: '#bdc3c7', textDecoration: 'none', fontSize: '12px' }}>
                    <i className="fas fa-question-circle me-2"></i>Help Center
                  </a>
                </li>
                */}
              </ul>
            </div>

            <div style={{
              flex: isDesktop ? '0 0 auto' : '0 0 100%',
              maxWidth: isDesktop ? '30%' : '100%',
              textAlign: isDesktop ? 'left' : 'center'
            }}>
              <h6 style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px', color: '#ecf0f1' }}>Business Hours</h6>
              <p style={{ fontSize: '12px', color: '#95a5a6', marginBottom: '5px', lineHeight: '1.5' }}>
                Monday - Friday: 8:00 AM - 6:00 PM EST<br />
                Saturday: 9:00 AM - 3:00 PM EST<br />
                Sunday: Closed
              </p>
            </div>

            <div style={{
              flex: isDesktop ? '0 0 auto' : '0 0 100%',
              maxWidth: isDesktop ? '25%' : '100%',
              textAlign: isDesktop ? 'left' : 'center'
            }}>
              <h6 style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px', color: '#ecf0f1' }}>Connect</h6>
              <div style={{ display: 'flex', gap: '15px', justifyContent: isDesktop ? 'flex-start' : 'center', marginBottom: '10px' }}>
                <a href="https://www.linkedin.com/company/purehd/" target="_blank" rel="noopener noreferrer"
                   style={{ color: '#4D64C1', fontSize: '20px' }}>
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a href="https://www.purehd.com" target="_blank" rel="noopener noreferrer"
                   style={{ color: '#4D64C1', fontSize: '20px' }}>
                  <i className="fas fa-globe"></i>
                </a>
              </div>
              <address style={{ fontSize: '12px', color: '#95a5a6', marginBottom: 0, fontStyle: 'normal', lineHeight: '1.5' }}>
                3 Mill & Main Place, Suite #200<br />
                Maynard, MA 01754-2657
              </address>
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: '20px',
            paddingTop: '15px',
            borderTop: '1px solid #34495e',
            fontSize: '11px',
            color: '#95a5a6'
          }}>
            &copy; {new Date().getFullYear()} PureHD E-Commerce Platform. All Rights Reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

// CSS for proper image sizing
const styleSheet = `
  .cart-item-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = styleSheet;
  if (!document.head.querySelector('style[data-component="SharedDraft"]')) {
    styleElement.setAttribute('data-component', 'SharedDraft');
    document.head.appendChild(styleElement);
  }
}

export default SharedDraft;